import { Button } from '@/components/ui/button';
import { MarkdownText } from '@/shared/MarkdownText';
import { cn } from '@/utils/classUtils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { FC, useLayoutEffect, useRef, useState } from 'react';

type UpdateSummaryMarkdownProps = {
  markdown: string;
};

export const UpdateSummaryMarkdown: FC<UpdateSummaryMarkdownProps> = ({
  markdown,
}) => {
  const trimmed = markdown.trim();
  const [expanded, setExpanded] = useState(false);
  const clipRef = useRef<HTMLDivElement>(null);
  const [needsToggle, setNeedsToggle] = useState(false);

  useLayoutEffect(() => {
    if (expanded) {
      setNeedsToggle(true);
      return;
    }
    const el = clipRef.current;
    if (!el) return;
    setNeedsToggle(el.scrollHeight > el.clientHeight + 1);
  }, [expanded, trimmed]);

  if (!trimmed) {
    return null;
  }

  return (
    <div className="flex w-full flex-col items-stretch gap-0">
      <div
        ref={clipRef}
        className={cn(
          'min-w-0 w-full text-sm leading-[1.44]',
          !expanded && 'max-h-[14.4em] overflow-hidden'
        )}
      >
        <MarkdownText variant="updateDetail">{trimmed}</MarkdownText>
      </div>
      {needsToggle && (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="self-end text-neutral-600 hover:text-neutral-900"
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? 'View less' : 'View more'}
          {expanded ? (
            <ChevronUp className="size-3.5 shrink-0" aria-hidden />
          ) : (
            <ChevronDown className="size-3.5 shrink-0" aria-hidden />
          )}
        </Button>
      )}
    </div>
  );
};
